import Cookies from "js-cookie";
import Path from "./Path";
import { getAuthSessionId, parseAuthJwt } from "./authSession";

const ACTIVITY_TRACKING_ENABLED =
	typeof process !== "undefined" && process.env?.REACT_APP_ACTIVITY_TRACKING_ENABLED === "false"
		? false
		: true;

const MAX_QUEUE = 200;
const FLUSH_MS = 5000;
const FLUSH_COUNT = 20;
const MAX_BODY = 60 * 1024;

let queue = [];
let timer = null;
let flushing = false;

const uuid = () => {
	if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
};

const sessionIdForFlush = () => {
	const token = Cookies.get("_cb_search_token");
	const auth = parseAuthJwt(token);
	if (auth?.userId != null) return getAuthSessionId(auth.userId);
	return uuid();
};

const categoryFor = (eventType) => {
	if (eventType.startsWith("navigation.")) return "navigation";
	if (eventType.startsWith("ui.")) return "ui";
	if (eventType.startsWith("api.")) return "api";
	return "ui";
};

export const track = (partial) => {
	if (!ACTIVITY_TRACKING_ENABLED) return;
	if (!Cookies.get("_cb_search_token")) return;
	if (!partial?.eventType) return;
	const event = {
		eventId: uuid(),
		schemaVersion: 1,
		eventType: partial.eventType,
		eventCategory: partial.eventCategory || categoryFor(partial.eventType),
		action: partial.action || null,
		pagePath: partial.pagePath || (typeof window !== "undefined" ? window.location.pathname : null),
		pageTitle: partial.pageTitle || (typeof document !== "undefined" ? document.title : null),
		component: partial.component || null,
		status: partial.status || "completed",
		metadata: partial.metadata || {},
		clientTimestamp: new Date().toISOString(),
		httpMethod: partial.httpMethod || null,
		apiEndpoint: partial.apiEndpoint || null,
		httpStatusCode: partial.httpStatusCode ?? null,
		errorMessage: partial.errorMessage || null,
		referrer: typeof document !== "undefined" ? document.referrer : null,
	};
	queue.push(event);
	if (queue.length > MAX_QUEUE) {
		queue = queue.slice(queue.length - MAX_QUEUE);
	}
	if (queue.length >= FLUSH_COUNT) flush();
	else ensureTimer();
};

export const trackPageView = (path) => {
	track({
		eventType: "navigation.page.viewed",
		eventCategory: "navigation",
		action: "Page viewed",
		pagePath: path || window.location.pathname,
		status: "completed",
	});
};

export const trackClick = (activityId) => {
	track({
		eventType: "ui.control.clicked",
		eventCategory: "ui",
		action: activityId,
		component: activityId,
		status: "completed",
	});
};

const ensureTimer = () => {
	if (timer) return;
	timer = setTimeout(() => {
		timer = null;
		flush();
	}, FLUSH_MS);
};

export const flush = async () => {
	if (!ACTIVITY_TRACKING_ENABLED || flushing || !queue.length) return;
	if (!Cookies.get("_cb_search_token")) {
		queue = [];
		return;
	}
	flushing = true;
	const batchId = uuid();
	const websiteId = Cookies.get("_website_id") ? Number(Cookies.get("_website_id")) : null;
	let events = queue.splice(0, queue.length);
	try {
		while (events.length) {
			let chunk = [];
			let size = 0;
			while (events.length) {
				const next = events[0];
				const add = JSON.stringify(next).length;
				if (chunk.length && size + add > MAX_BODY) break;
				chunk.push(events.shift());
				size += add;
			}
			const url = Path("ms1") + "api/v1/activity/batch";
			const token = Cookies.get("_cb_search_token");
			await fetch(url, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({ batchId, sessionId: sessionIdForFlush(), websiteId, events: chunk }),
				keepalive: true,
			}).catch(() => {});
		}
	} finally {
		flushing = false;
		if (queue.length) ensureTimer();
	}
};

export const initActivityTracker = () => {
	if (!ACTIVITY_TRACKING_ENABLED || typeof document === "undefined") return;
	document.addEventListener(
		"click",
		(e) => {
			const el = e.target?.closest?.("[data-activity]");
			if (el) trackClick(el.getAttribute("data-activity"));
		},
		true
	);
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "hidden") flush();
	});
	window.addEventListener("pagehide", () => {
		flush();
	});
};

export default { track, trackPageView, trackClick, flush, initActivityTracker };
